<!-- src/components/CreateSeasonModal.vue -->
<template>
  <div
    class="modal fade"
    id="createSeasonModal"
    tabindex="-1"
    aria-labelledby="createSeasonModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="createSeasonModalLabel">Create Season</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="createSeason">
            <div class="mb-3">
              <label for="seasonDescription" class="form-label">Name</label>
              <input
                type="text"
                class="form-control"
                id="seasonDescription"
                v-model="description"
                required
                placeholder="Enter season name"
              />
            </div>
            <div class="row mb-3">
              <div class="col-md-6">
                <label for="seasonStartDate" class="form-label"
                  >Start Date</label
                >
                <input
                  type="date"
                  class="form-control"
                  id="seasonStartDate"
                  v-model="startDate"
                  :min="today"
                  required
                />
              </div>
              <div class="col-md-6">
                <label for="seasonEndDate" class="form-label">End Date</label>
                <input
                  type="date"
                  class="form-control"
                  id="seasonEndDate"
                  v-model="endDate"
                  :min="startDate || today"
                  required
                />
              </div>
            </div>
            <div class="form-check form-check-container mb-3">
              <input
                class="form-check-input"
                type="checkbox"
                id="overrideSeasonCreate"
                v-model="overrideSeason"
              />
              <label class="form-check-label" for="overrideSeasonCreate">
                Override Season
              </label>
            </div>
            <div v-if="errorMessage" class="alert alert-danger" role="alert">
              {{ errorMessage }}
            </div>
            <div class="d-flex justify-content-end">
              <button
                type="button"
                class="btn btn-secondary me-2"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button type="submit" class="btn btn-primary">Create</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  PropType,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { Modal } from 'bootstrap';
import { Season, SeasonBatchSaveRequest } from '../api';
import { seasonBatchApi } from '@/apiClient';
import { validateSeasonName } from '@/utils/seasonUtils';

export default defineComponent({
  name: 'CreateSeasonModal',
  props: {
    courseId: {
      type: Number as PropType<number>,
      required: true,
    },
    existingSeasons: {
      type: Array as PropType<Season[]>,
      required: true,
    },
  },
  emits: ['season-created'],
  setup(props, { emit }) {
    const description = ref('');
    const startDate = ref('');
    const endDate = ref('');
    const errorMessage = ref('');
    const overrideSeason = ref(false);

    const today = computed(() => {
      const date = new Date();
      return date.toISOString().split('T')[0];
    });

    const checkDateOverlap = (start: string, end: string) => {
      const newStart = new Date(start).toISOString();
      const newEnd = new Date(end).toISOString();

      return props.existingSeasons.some((season) => {
        const seasonStart = season.startDate
          ? new Date(season.startDate.split('T')[0]).toISOString()
          : null;
        const seasonEnd = season.endDate
          ? new Date(season.endDate.split('T')[0]).toISOString()
          : null;

        if (!seasonStart || !seasonEnd) {
          return false;
        }

        return (
          (newStart >= seasonStart && newStart <= seasonEnd) ||
          (newEnd >= seasonStart && newEnd <= seasonEnd) ||
          (newStart <= seasonStart && newEnd >= seasonEnd)
        );
      });
    };

    const validateDates = () => {
      if (startDate.value < today.value) {
        errorMessage.value = 'Start date cannot be in the past.';
        return false;
      }
      if (endDate.value < startDate.value) {
        errorMessage.value = 'End date cannot be before the start date.';
        return false;
      }

      if (
        !overrideSeason.value &&
        checkDateOverlap(startDate.value, endDate.value)
      ) {
        errorMessage.value =
          'The selected dates overlap with an existing season.';
        return false;
      }
      errorMessage.value = '';
      return true;
    };

    const resetModal = () => {
      description.value = '';
      startDate.value = '';
      endDate.value = '';
      errorMessage.value = '';
      overrideSeason.value = false;
    };

    const createSeason = async () => {
      if (
        !validateDates() ||
        !validateSeasonName(
          description.value,
          props.existingSeasons,
          errorMessage
        )
      ) {
        return;
      }

      try {
        console.log(`Creating season ${description.value}`);

        const newSeason: Partial<Season> = {
          description: description.value,
          startDate: startDate.value,
          endDate: endDate.value,
          priority: overrideSeason.value ? 1 : 0,
          seasonChannels: [],
        };

        const seasonBatchSaveRequest: SeasonBatchSaveRequest = {
          editedSeasons: [newSeason],
          deletedSeasonIds: [],
        };

        await seasonBatchApi.seasonBatchSavePut(
          props.courseId,
          seasonBatchSaveRequest
        );

        emit('season-created');

        // Reset form fields and close modal
        resetModal();

        const modalElement = document.getElementById('createSeasonModal');
        if (modalElement) {
          const bootstrapModal =
            Modal.getInstance(modalElement) || new Modal(modalElement);
          bootstrapModal.hide();
        }
      } catch (error) {
        console.error('Error creating season:', error);
        errorMessage.value = 'An error occurred while creating the season.';
      }
    };

    const handleModalClose = () => {
      setTimeout(resetModal, 300);
    };

    onMounted(() => {
      const modalElement = document.getElementById('createSeasonModal');
      if (modalElement) {
        modalElement.addEventListener('hidden.bs.modal', handleModalClose);
      }
    });

    onBeforeUnmount(() => {
      const modalElement = document.getElementById('createSeasonModal');
      if (modalElement) {
        modalElement.removeEventListener('hidden.bs.modal', handleModalClose);
      }
    });

    return {
      overrideSeason,
      description,
      startDate,
      endDate,
      createSeason,
      errorMessage,
      today,
      checkDateOverlap,
    };
  },
});
</script>

<style scoped>
.modal-dialog-centered {
  display: flex;
  align-items: center;
  min-height: calc(100% - 1rem);
}

.modal-content {
  border-radius: 8px;
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
}

.modal-title {
  font-weight: 600;
}

.form-label {
  display: block;
  text-align: left;
}

.form-check-container {
  text-align: left;
}

.form-check-label {
  margin-left: 5px;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
}

.btn-primary:hover {
  background-color: #0056b3;
  border-color: #004085;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-secondary:hover {
  background-color: #5a6268;
  border-color: #545b62;
}
</style>
